import {
  CoachOnboardingEventListbyCAM,
  OnboardingChecklistbyCoach,
  getOnboardingChecklistByCoach,
  ContentURLsChecklistbyCoach,
  ManualCoachEventList,
  HistoryEventType,
} from '../../../../../Model/CoachHistory';

/**
 * Util function to identify pending events, separated into manual, coach tasks, and CAM tasks.
 *
 * @param {Array} history - Array of recorded history events of the coach.
 * @param {Boolean} isNewCoach - Whether the coach is a new coach.
 * @param {Boolean} isEnrolledInISProgram - Whether the coach is enrolled in the IS program.
 * @returns {Object} Pending manual, coach tasks, and CAM tasks.
 */
const getPendingEvents = (history, isNewCoach, isEnrolledInISProgram) => {
  const completedEventTypes = new Set(history.map((event) => event.type));

  const manualEvents = [];
  const coachTasks = [];
  const camTasks = [];
  const contentURLSubTasks = [];

  const accountCreatedAt = history.find((event) => event.type === HistoryEventType.ACCOUNT_CREATED)?.createdAt;
  const coachAutomaticEventList = isNewCoach ? getOnboardingChecklistByCoach(accountCreatedAt) : [
    HistoryEventType.PROFILE_SET,
    HistoryEventType.STRIPE_CONNECTED,
  ];

  // Process both lists
  [...CoachOnboardingEventListbyCAM, ...coachAutomaticEventList].forEach((eventType) => {
    if (!completedEventTypes.has(eventType)) {
      if (ManualCoachEventList.includes(eventType)) {
        manualEvents.push(eventType);
      } else if (OnboardingChecklistbyCoach.includes(eventType)) {
        // Skip IS_AC_QUESTIONNAIRE_SUBMITTED if coach is enrolled in IS program
        if (eventType === HistoryEventType.IS_AC_QUESTIONNAIRE_SUBMITTED && isEnrolledInISProgram) {
          return;
        }
        coachTasks.push(eventType);
      } else if (CoachOnboardingEventListbyCAM.includes(eventType)) {
        camTasks.push(eventType);
      }
    }
  });

  // Process content URL sub-tasks separately
  ContentURLsChecklistbyCoach.forEach((eventType) => {
    if (!completedEventTypes.has(eventType)) {
      contentURLSubTasks.push(eventType);
    }
  });

  return {
    manualEvents,
    coachTasks,
    camTasks,
    contentURLSubTasks,
  };
};

const getOnboardingChecklistAnswers = (history) => {
  const answers = {};

  history.forEach((event) => {
    switch (event.type) {
      case HistoryEventType.IS_AC_QUESTIONNAIRE_SUBMITTED:
        answers.isAcQuestionnaire = event.metadata;
        break;

      case HistoryEventType.CONTENT_URLS_SUBMITTED:
        answers.contentURLs = { ...(event.metadata || {}) };
        break;

      case HistoryEventType.TRANSFORMATION_POST_SUBMITTED:
        // Only set if contentURLs not already set by CONTENT_URLS_SUBMITTED
        if (!answers.contentURLs) {
          answers.contentURLs = {
            transformationPostUrl: event.metadata?.transformationPostUrl,
          };
        }
        break;

      case HistoryEventType.LAUNCH_POST_SUBMITTED:
        // Only set if contentURLs not already set by CONTENT_URLS_SUBMITTED
        if (!answers.contentURLs) {
          answers.contentURLs = {
            launchPostUrl: event.metadata?.launchPostUrl,
          };
        }
        break;

      case HistoryEventType.COACH_ONBOARDING_FORM_COMPLETED:
        answers.coachOnboardingForm = true;
        break;

      default:
        break;
    }
  });

  return answers;
};

export { getPendingEvents, getOnboardingChecklistAnswers };
